import { ArrowUp, ArrowDown, Eye, EyeOff } from "lucide-react";
import { SECTION_LABELS } from "../../lib/content";
import { DEFAULT_LOGO } from "../site/Logo";
import { ImageField } from "./ImageField";
import { inputCls } from "./Field";

const ColorField = ({ label, value, onChange, fallback, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">{label}</span>
    <div className="flex items-center gap-3">
      <input type="color" value={value || fallback} onChange={(e) => onChange(e.target.value)} className="h-11 w-14 cursor-pointer rounded-lg border border-white/10 bg-transparent" data-testid={`${testid}-picker`} />
      <input className={inputCls} value={value || fallback} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-hex`} />
      <button type="button" onClick={() => onChange(fallback)} className="shrink-0 rounded-full border border-white/10 px-3 py-2 text-xs text-zinc-300 hover:border-white/30" data-testid={`${testid}-reset`}>Default</button>
    </div>
  </label>
);

export const LookEditor = ({ theme, images, onTheme, onImages }) => (
  <div className="grid gap-4 lg:grid-cols-2" data-testid="admin-look-editor">
    <div className="space-y-5 rounded-2xl border border-white/10 bg-[#12121A] p-5">
      <h3 className="font-display text-lg font-bold text-white">Brand</h3>
      <ImageField label="Logo (white or light logo works best on the dark site)" value={theme.logo} fallback={DEFAULT_LOGO} contain
        onChange={(v) => onTheme({ ...theme, logo: v })} testid="admin-theme-logo" />
      <ColorField label="Main accent colour (buttons, highlights)" value={theme.accent} fallback="#CCFF00" onChange={(v) => onTheme({ ...theme, accent: v })} testid="admin-theme-accent" />
      <ColorField label="Second accent colour (labels, glows)" value={theme.accent2} fallback="#FF2D7B" onChange={(v) => onTheme({ ...theme, accent2: v })} testid="admin-theme-accent2" />
    </div>
    <div className="space-y-5 rounded-2xl border border-white/10 bg-[#12121A] p-5">
      <h3 className="font-display text-lg font-bold text-white">Page photos</h3>
      <ImageField label="Top banner photo (behind the title)" value={images.hero} fallback="/img/hero.jpg" onChange={(v) => onImages({ ...images, hero: v })} testid="admin-image-hero" />
      <ImageField label="Dillusions section photo" value={images.programs} fallback="/img/group.jpg" onChange={(v) => onImages({ ...images, programs: v })} testid="admin-image-programs" />
      <p className="text-xs text-zinc-500">Class photos are changed on each class in the "Classes & times" tab.</p>
    </div>
  </div>
);

export const SectionsEditor = ({ sections, onChange }) => {
  const move = (i, d) => {
    const l = [...sections];
    [l[i], l[i + d]] = [l[i + d], l[i]];
    onChange(l);
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-[#12121A] p-5" data-testid="admin-sections-editor">
      <p className="mb-4 text-sm text-zinc-400">The weekly timetable always stays at the top. Reorder or hide the sections below it.</p>
      <ul className="space-y-2">
        {sections.map((s, i) => (
          <li key={s.id} className={`flex items-center justify-between gap-3 rounded-xl border border-white/10 px-4 py-3 ${s.visible ? "" : "opacity-50"}`} data-testid={`admin-section-${s.id}`}>
            <span className="flex items-center gap-3">
              <span className="font-mono text-xs text-zinc-500">0{i + 1}</span>
              <span className="font-semibold text-white">{SECTION_LABELS[s.id]}</span>
              {!s.visible && <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] text-zinc-300">Hidden</span>}
            </span>
            <span className="flex gap-1.5">
              <button disabled={i === 0} onClick={() => move(i, -1)} className="grid h-9 w-9 place-items-center rounded-lg border border-white/10 text-zinc-300 disabled:opacity-30" aria-label="Move up" data-testid={`admin-section-${s.id}-up`}><ArrowUp size={15} /></button>
              <button disabled={i === sections.length - 1} onClick={() => move(i, 1)} className="grid h-9 w-9 place-items-center rounded-lg border border-white/10 text-zinc-300 disabled:opacity-30" aria-label="Move down" data-testid={`admin-section-${s.id}-down`}><ArrowDown size={15} /></button>
              <button onClick={() => onChange(sections.map((x, j) => (j === i ? { ...x, visible: !x.visible } : x)))} className="grid h-9 w-9 place-items-center rounded-lg border border-white/10 text-zinc-300 hover:text-white" aria-label={s.visible ? "Hide" : "Show"} data-testid={`admin-section-${s.id}-toggle`}>
                {s.visible ? <Eye size={15} /> : <EyeOff size={15} />}
              </button>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
